import { useState, useCallback, useEffect, useRef } from 'react';
import { Station } from '../../types/station';
import { Customer } from '../../types/customer';
import stationsService from '../../services/ScaleoAdminApiServices/StationsService';
import recognitionsService from '../../services/ScaleoAdminApiServices/RecognitionsService';
import customersService from '../../services/ScaleoAdminApiServices/CustomersService';
import locationsService from '../../services/ScaleoAdminApiServices/LocationsService';
import { Recognition } from '../../types/recognition';

export const useRecognitionsData = () => {
  const [componentData, setComponentData] = useState<{
    recognitions: Recognition[];
    stations: Station[];
    customers: { id: string; companyName: string }[];
    locations: { id: string; name: string; customerId: string }[];
  }>({
    recognitions: [],
    stations: [],
    customers: [],
    locations: [],
  });
  const [filters, setFilters] = useState<any>({});
  const [internalFilters, setInternalFilters] = useState(filters);
  const [paginationParameters, setPaginationParameters] = useState(emptyPaginationParameters);
  const [paginatorState, setPaginatorState] = useState(emptyPaginatorState);
  const debounceTimerRef = useRef<any>(null);

  const fetchRecognitions = useCallback(
    async (_filters: any, _paginationParameters: any) => {
      void Promise.all([
        recognitionsService.searchRecognitions(_filters, _paginationParameters),
        stationsService.getStations(),
        customersService.searchCustomers(),
        locationsService.getAllLocations(),
      ]).then((response) => {
        const customers = (response[2].data?.items || []).map((c: Customer) => ({
          id: c.id,
          companyName: c.companyName,
        }));

        const locations = (response[3].data?.data || []).map((l: any) => ({
          id: l.id,
          name: l.name || l.shortName,
          customerId: l.customer?.id,
        }));

        setComponentData({
          recognitions: response[0].data.data,
          stations: response[1].data.map((s: Station) => ({
            ...s,
            nameInstallationId: s.installationId ? `${s.name} [${s.installationId}]` : s.name,
          })),
          customers,
          locations,
        });

        setPaginatorState({
          ...paginatorState,
          totalPages: response[0].data.totalPagesNumber,
          currentPage: paginatorState.currentPage > response[0].data.totalPagesNumber ? 1 : paginatorState.currentPage,
        });
      });
    },
    [
      recognitionsService.searchRecognitions,
      stationsService.getStations,
      setComponentData,
      setPaginatorState,
      JSON.stringify(paginatorState),
    ],
  );

  useEffect(() => {
    if (
      typeof internalFilters?.station !== 'string' &&
      typeof internalFilters?.customer !== 'string' &&
      typeof internalFilters?.location !== 'string'
    ) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        void fetchRecognitions(internalFilters, paginationParameters);
      }, 100);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [JSON.stringify(internalFilters), JSON.stringify(paginationParameters)]);

  useEffect(() => {
    if (
      paginatorState.currentPage !== paginationParameters.page.index ||
      paginatorState.rows !== paginationParameters.page.size
    ) {
      const params = {
        ...paginationParameters,
        page: {
          index: paginatorState.currentPage,
          size: paginatorState.rows,
        },
      };
      setPaginationParameters(params);
    }
  }, [paginationParameters, paginatorState]);

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1 ? true : false,
        orderColumn: e.sortField,
      },
    });
  };

  const changeFilter = (value: any, name: string) => {
    const newFilters: any = { ...internalFilters };
    newFilters[name] = value;

    if (name === 'station') {
      if (typeof value !== 'string') {
        newFilters.stationId = value?.id || null;
      }
    }

    if (name === 'customer') {
      if (typeof value !== 'string') {
        newFilters.customerId = value?.id || null;
        newFilters.location = null;
        newFilters.locationId = null;
      }
    }

    if (name === 'location') {
      if (typeof value !== 'string') {
        newFilters.locationId = value?.id || null;
      }
    }

    setFilters(newFilters);
    setInternalFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setInternalFilters({});
  };

  const refresh = () => {
    void fetchRecognitions(internalFilters, paginationParameters);
  };

  return {
    recognitions: componentData.recognitions,
    stations: componentData.stations,
    customers: componentData.customers,
    locations: componentData.locations,
    handleSort,
    paginationParameters,
    paginatorState,
    setPaginatorState,
    internalFilters,
    changeFilter,
    clearFilters,
    refresh,
    filters,
  };
};

const emptyPaginationParameters = {
  order: {
    isAscending: false,
    orderColumn: 'createdAt',
  },
  page: {
    index: 1,
    size: 10,
  },
};

const emptyPaginatorState = {
  currentPage: 1,
  totalPages: 1,
  rows: 10,
};
